import { formatRateFieldPath } from "@platform/btx-rates";

import {
  COVERAGE_HEADING_LABEL,
  COVERAGE_HEADING_ORDER,
  NETWORK_LABEL,
  normalizeToHeadingCoverageType,
} from "./constants";

import type { CoverageTypeEnum } from "@platform/types";
import type { ResolvedComment } from "./resolveComments";

export interface CommentBullet {
  id: string;
  benefitLabel: string;
  comment: string;
  /** Present only for a Quotes-sourced bullet — lets the clipboard formatter
   * split a dual/tri-option plan group's bullets by option. */
  proposalPlanId?: string;
}

export interface PlanGroupSection {
  key: string;
  label: string;
  bullets: CommentBullet[];
}

export interface CoverageSection {
  coverageType: CoverageTypeEnum;
  heading: string;
  /** False when the coverage has only one RFP-requested plan group — the
   * AC's plan-group sub-header would then just repeat the coverage heading. */
  showPlanGroupHeaders: boolean;
  planGroups: PlanGroupSection[];
}

export interface NavEntry {
  key: string;
  label: string;
}

export interface CarrierCommentDocument {
  carrierId: string;
  carrierName: string;
  count: number;
  coverages: CoverageSection[];
  comps: PlanGroupSection[];
  responses: PlanGroupSection[];
  navEntries: NavEntry[];
}

export interface CommentDocument {
  carriers: CarrierCommentDocument[];
  totalCount: number;
}

export interface CoverageRef {
  planGroupId: string;
  /** Base LOC coverage type, e.g. "dental" — Responses/Comps plan groups are
   * not split into Dental PPO/HMO the way Quotes/SBS pages are. */
  coverageType: string;
  label: string;
  sequence: number;
}

export interface CarrierRef {
  id: string;
  name: string;
  sequence: number;
}

export interface CompsSubsidyRowRef {
  kind: string;
  label: string;
  sequence: number;
}

export interface CompsRowLabelData {
  coverageRefs: readonly CoverageRef[];
  subsidyRows: readonly CompsSubsidyRowRef[];
  notesLabel: string;
}

export interface BenefitOrderIndex {
  order: ReadonlyMap<string, number>;
  label: ReadonlyMap<string, string>;
}

export interface BuildCommentDocumentInput {
  comments: readonly ResolvedComment[];
  carriers: readonly CarrierRef[];
  /** The Responses table's coverages — also used to resolve a Quotes
   * comment's plan-group label/sequence and to count how many plan groups a
   * coverage has (for the plan-group sub-header rule). */
  responsesCoverages: readonly CoverageRef[];
  comps: CompsRowLabelData;
  /** Keyed by the raw stored LOC coverage type (e.g. "dental-ppo") — the
   * `flattenBenefitDefinitions` + `buildCollapseAmountRows` order/label for
   * that coverage's benefit table. */
  benefitOrderByCoverageType: ReadonlyMap<string, BenefitOrderIndex>;
}

const SUBSIDY_ROW_TYPE = "subsidy";
const NOTES_ROW_GROUP_ID = "notes";
const AMOUNT_ROW_TYPE = "amount";
const UNRANKED = Number.MAX_SAFE_INTEGER;
// Coverage rows sort by their own sequence (small numbers); subsidy rows and
// the Notes row sort after every coverage row, in that order, mirroring
// buildCompsGridData's row order (coverages, then subsidy, then notes).
const UNRANKED_STEP = 1_000_000;

const byRank =
  <T>(rankOf: (item: T) => number) =>
  (a: T, b: T): number =>
    rankOf(a) - rankOf(b);

const benefitLabelOf = (
  comment: Extract<ResolvedComment, { source: "quotes" }>,
  benefitOrderByCoverageType: ReadonlyMap<string, BenefitOrderIndex>,
): string => {
  // A rate review stores its whole coordinate in benefitKey, so it is absent
  // from the benefit-definition index and would otherwise print raw.
  const rateLabel = formatRateFieldPath(comment.benefitKey);
  if (rateLabel) {
    return rateLabel;
  }

  const index = benefitOrderByCoverageType.get(comment.coverageType);
  const baseLabel = index?.label.get(comment.benefitKey) ?? comment.benefitKey;
  const networkLabel = NETWORK_LABEL[comment.network];
  return networkLabel ? `${baseLabel} (${networkLabel})` : baseLabel;
};

const benefitRankOf = (
  comment: Extract<ResolvedComment, { source: "quotes" }>,
  benefitOrderByCoverageType: ReadonlyMap<string, BenefitOrderIndex>,
): number =>
  benefitOrderByCoverageType
    .get(comment.coverageType)
    ?.order.get(comment.benefitKey) ?? UNRANKED;

const buildQuotesCoverages = (
  quotesComments: readonly Extract<ResolvedComment, { source: "quotes" }>[],
  responsesCoverages: readonly CoverageRef[],
  benefitOrderByCoverageType: ReadonlyMap<string, BenefitOrderIndex>,
): CoverageSection[] => {
  const coverageRefByPlanGroupId = new Map(
    responsesCoverages.map((ref) => [ref.planGroupId, ref]),
  );

  const planGroupCountByHeadingType = new Map<CoverageTypeEnum, number>();
  for (const ref of responsesCoverages) {
    const heading = normalizeToHeadingCoverageType(ref.coverageType);
    if (!heading) continue;
    planGroupCountByHeadingType.set(
      heading,
      (planGroupCountByHeadingType.get(heading) ?? 0) + 1,
    );
  }

  const byHeading = new Map<
    CoverageTypeEnum,
    Map<string, Extract<ResolvedComment, { source: "quotes" }>[]>
  >();

  for (const comment of quotesComments) {
    const heading = normalizeToHeadingCoverageType(comment.coverageType);
    if (!heading) continue;

    const byPlanGroup =
      byHeading.get(heading) ??
      new Map<string, Extract<ResolvedComment, { source: "quotes" }>[]>();
    byHeading.set(heading, byPlanGroup);

    const group = byPlanGroup.get(comment.planGroupId) ?? [];
    group.push(comment);
    byPlanGroup.set(comment.planGroupId, group);
  }

  return COVERAGE_HEADING_ORDER.filter((heading) => byHeading.has(heading)).map(
    (heading) => {
      const byPlanGroup = byHeading.get(heading)!;
      const planGroupIds = [...byPlanGroup.keys()].toSorted(
        byRank(
          (planGroupId) =>
            coverageRefByPlanGroupId.get(planGroupId)?.sequence ?? UNRANKED,
        ),
      );

      const planGroups: PlanGroupSection[] = planGroupIds.map((planGroupId) => {
        const bullets = byPlanGroup
          .get(planGroupId)!
          .toSorted(
            byRank((comment) =>
              benefitRankOf(comment, benefitOrderByCoverageType),
            ),
          )
          .map(
            (comment): CommentBullet => ({
              id: comment.id,
              benefitLabel: benefitLabelOf(comment, benefitOrderByCoverageType),
              comment: comment.comment,
              proposalPlanId: comment.proposalPlanId,
            }),
          );

        return {
          key: planGroupId,
          label:
            coverageRefByPlanGroupId.get(planGroupId)?.label ?? planGroupId,
          bullets,
        };
      });

      return {
        coverageType: heading,
        heading: COVERAGE_HEADING_LABEL[heading],
        showPlanGroupHeaders:
          (planGroupCountByHeadingType.get(heading) ?? 0) > 1,
        planGroups,
      };
    },
  );
};

const buildResponsesSections = (
  responsesComments: readonly Extract<
    ResolvedComment,
    { source: "responses" }
  >[],
  responsesCoverages: readonly CoverageRef[],
): PlanGroupSection[] => {
  const coverageRefByPlanGroupId = new Map(
    responsesCoverages.map((ref) => [ref.planGroupId, ref]),
  );

  const byPlanGroup = new Map<
    string,
    Extract<ResolvedComment, { source: "responses" }>[]
  >();
  for (const comment of responsesComments) {
    const group = byPlanGroup.get(comment.planGroupId) ?? [];
    group.push(comment);
    byPlanGroup.set(comment.planGroupId, group);
  }

  return [...byPlanGroup.keys()]
    .toSorted(
      byRank(
        (planGroupId) =>
          coverageRefByPlanGroupId.get(planGroupId)?.sequence ?? UNRANKED,
      ),
    )
    .map((planGroupId) => {
      const label =
        coverageRefByPlanGroupId.get(planGroupId)?.label ?? planGroupId;
      return {
        key: planGroupId,
        label,
        bullets: byPlanGroup.get(planGroupId)!.map(
          (comment): CommentBullet => ({
            id: comment.id,
            benefitLabel: label,
            comment: comment.comment,
          }),
        ),
      };
    });
};

const compsBulletLabel = (rowType: string, rowGroupLabel: string): string =>
  rowType === AMOUNT_ROW_TYPE ? "Amount" : rowGroupLabel;

const buildCompsSections = (
  compsComments: readonly Extract<ResolvedComment, { source: "comps" }>[],
  comps: CompsRowLabelData,
): PlanGroupSection[] => {
  const coverageRefByPlanGroupId = new Map(
    comps.coverageRefs.map((ref) => [ref.planGroupId, ref]),
  );
  const subsidyByKind = new Map(
    comps.subsidyRows.map((row) => [row.kind, row]),
  );

  const rowGroupLabelOf = (rowGroupId: string): string => {
    if (rowGroupId === NOTES_ROW_GROUP_ID) return comps.notesLabel;
    const subsidyKind = rowGroupId.startsWith(`${SUBSIDY_ROW_TYPE}:`)
      ? rowGroupId.slice(SUBSIDY_ROW_TYPE.length + 1)
      : undefined;
    if (subsidyKind) return subsidyByKind.get(subsidyKind)?.label ?? rowGroupId;
    return coverageRefByPlanGroupId.get(rowGroupId)?.label ?? rowGroupId;
  };

  const rowGroupRankOf = (rowGroupId: string): number => {
    if (rowGroupId === NOTES_ROW_GROUP_ID) return 2 * UNRANKED_STEP;
    const subsidyKind = rowGroupId.startsWith(`${SUBSIDY_ROW_TYPE}:`)
      ? rowGroupId.slice(SUBSIDY_ROW_TYPE.length + 1)
      : undefined;
    if (subsidyKind) {
      return UNRANKED_STEP + (subsidyByKind.get(subsidyKind)?.sequence ?? 0);
    }
    return coverageRefByPlanGroupId.get(rowGroupId)?.sequence ?? UNRANKED;
  };

  const byRowGroup = new Map<
    string,
    Extract<ResolvedComment, { source: "comps" }>[]
  >();
  for (const comment of compsComments) {
    const group = byRowGroup.get(comment.rowGroupId) ?? [];
    group.push(comment);
    byRowGroup.set(comment.rowGroupId, group);
  }

  return [...byRowGroup.keys()]
    .toSorted(byRank(rowGroupRankOf))
    .map((rowGroupId) => {
      const rowGroupLabel = rowGroupLabelOf(rowGroupId);
      const bullets = byRowGroup
        .get(rowGroupId)!
        .toSorted(
          byRank((comment) => (comment.rowType === AMOUNT_ROW_TYPE ? 1 : 0)),
        )
        .map(
          (comment): CommentBullet => ({
            id: comment.id,
            benefitLabel: compsBulletLabel(comment.rowType, rowGroupLabel),
            comment: comment.comment,
          }),
        );

      return { key: rowGroupId, label: rowGroupLabel, bullets };
    });
};

const navEntriesFor = (
  coverages: CoverageSection[],
  comps: PlanGroupSection[],
  responses: PlanGroupSection[],
): NavEntry[] => [
  ...coverages.map((section) => ({
    key: section.coverageType,
    label: section.heading,
  })),
  ...(comps.length > 0 ? [{ key: "comps", label: "Comps" }] : []),
  ...(responses.length > 0 ? [{ key: "responses", label: "Responses" }] : []),
];

/**
 * Builds the drawer's per-carrier document: Quotes/SBS comments grouped by
 * canonical coverage order and RFP-requested plan group (in benefit-table
 * row order), followed by a flat Comps section and a flat Responses section,
 * per the user story's acceptance criteria.
 */
export const buildCommentDocument = ({
  comments,
  carriers,
  responsesCoverages,
  comps,
  benefitOrderByCoverageType,
}: BuildCommentDocumentInput): CommentDocument => {
  const carrierRefById = new Map(
    carriers.map((carrier) => [carrier.id, carrier]),
  );

  const byCarrier = new Map<string, ResolvedComment[]>();
  for (const comment of comments) {
    const group = byCarrier.get(comment.carrierId) ?? [];
    group.push(comment);
    byCarrier.set(comment.carrierId, group);
  }

  const carrierDocuments = [...byCarrier.entries()]
    .toSorted(
      byRank(
        ([carrierId]) => carrierRefById.get(carrierId)?.sequence ?? UNRANKED,
      ),
    )
    .map(([carrierId, carrierComments]): CarrierCommentDocument => {
      const quotesComments = carrierComments.filter(
        (comment): comment is Extract<ResolvedComment, { source: "quotes" }> =>
          comment.source === "quotes",
      );
      const responsesComments = carrierComments.filter(
        (
          comment,
        ): comment is Extract<ResolvedComment, { source: "responses" }> =>
          comment.source === "responses",
      );
      const compsComments = carrierComments.filter(
        (comment): comment is Extract<ResolvedComment, { source: "comps" }> =>
          comment.source === "comps",
      );

      const coverages = buildQuotesCoverages(
        quotesComments,
        responsesCoverages,
        benefitOrderByCoverageType,
      );
      const responsesSections = buildResponsesSections(
        responsesComments,
        responsesCoverages,
      );
      const compsSections = buildCompsSections(compsComments, comps);

      return {
        carrierId,
        carrierName: carrierRefById.get(carrierId)?.name ?? carrierId,
        count: carrierComments.length,
        coverages,
        comps: compsSections,
        responses: responsesSections,
        navEntries: navEntriesFor(coverages, compsSections, responsesSections),
      };
    });

  return {
    carriers: carrierDocuments,
    totalCount: comments.length,
  };
};
